import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { Modal, ModalTitle, ModalWindow } from '../../../components/Modal';
import { useState } from 'react';
import { checkAppSumoFeature, PLANS } from 'zync-common/zyncCustomerPlans';
import { useWorkspacePlan } from '../../../hooks/useWorkspacePlan';
import {
  sendCustomWebhookApi,
  updateEpisodeSettingsApi,
} from '../../../helper/api';
import { TextInputRaw } from '../../../components/Input/TextInputRaw';
import { ArrowRightIcon } from '../../../components/icons/ArrowRightIcon';
import classNames from '../../../helper/classNames';
import { isValidUrl } from '../../Workspaces/WebhookIntegrations';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { CloudUploadIcon } from '../../../components/icons/CloudUploadIcon';
import moment from 'moment';
import { useTrackActivity } from '../../../hooks/track';

const formatPublishedDate = (date) => {
  if (!date) return null;
  const formatted = moment(date).format('MMMM D, YYYY h:mm A');
  return formatted;
};

const getCDNLink = (url) => {
  if (!url) return null;
  const NEW_STORAGE_PREFIX =
    'https://storage.googleapis.com/ai-recorder-media-assets';
  const NEW_STORAGE_CDN_PREFIX = 'https://cdn.solorecording.studio';
  const OLD_STORAGE_PREFIX = 'https://storage.googleapis.com/zync-media';
  const OLD_STORAGE_CDN_PREFIX = 'https://cdn.zync.ai';

  if (url.includes(OLD_STORAGE_PREFIX)) {
    return url.replace(OLD_STORAGE_PREFIX, OLD_STORAGE_CDN_PREFIX);
  }
  if (url.includes(NEW_STORAGE_PREFIX)) {
    return url.replace(NEW_STORAGE_PREFIX, NEW_STORAGE_CDN_PREFIX);
  }

  return url;
};

export const PublishToWebhookModal = ({
  handleClose,
  highlights = [],
  workspace,
  series,
  localDispatch,
  refreshSeries,
  contentKitData,
}) => {
  const track = useTrackActivity();
  const { workspaceId, integrations } = workspace || {};
  const { webhook } = integrations || {};
  const { webhookUrl } = webhook || {};
  const { plan, isAppSumo, activeSubscription, isTrial } = useWorkspacePlan();
  const isSoloPlan = plan === PLANS.solo.id;
  const isWebhookFeatureAvailable =
    (!isTrial && activeSubscription && isSoloPlan) ||
    (isAppSumo &&
      activeSubscription &&
      checkAppSumoFeature('customDomain', plan));

  const { lastPublishedCustomWebhookDate: contentKitLastPublishedDate } =
    contentKitData || {};

  const { meetingSeriesId, slug } = series || {};

  const firstHighlight = highlights[0];
  const {
    title,
    HIGHLIGHT_EDITED_SQUARE,
    HIGHLIGHT_EDITED_PORTRAIT,
    HIGHLIGHT_EDITED_LANDSCAPE,
    HIGHLIGHT_THUMBNAIL,
    HIGHLIGHT_PORTRAIT_THUMBNAIL,
    HIGHLIGHT_SQUARE_THUMBNAIL,
    sentences = [],
    highlightId,
  } = firstHighlight || {};

  const [settings, setSettings] = useState(series?.settings);
  const { webhook: seriesWebhookSettings } = settings || {};
  const { overrideWebhookUrl = '', lastPublishedDate } =
    seriesWebhookSettings || {};
  const [inputWebhookUrl, setInputWebhookUrl] = useState(
    overrideWebhookUrl || webhookUrl
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isValidWebhookUrl = isValidUrl(inputWebhookUrl);
  const isPublishButtonDisabled = isLoading || !isValidWebhookUrl;

  const handleSaveSeriesSettings = async () => {
    try {
      const lastPublishedDate = Date.now();
      const updatedSeriesWebhookSettings = { lastPublishedDate };

      if (inputWebhookUrl !== webhookUrl) {
        updatedSeriesWebhookSettings.overrideWebhookUrl = inputWebhookUrl;
      }

      const result = await updateEpisodeSettingsApi(meetingSeriesId, {
        webhook: updatedSeriesWebhookSettings,
      });

      if (result) {
        setSettings(result.settings);
        return result;
      } else {
        setError('Error saving settings, please try again');
        setIsLoading(false);
        return null;
      }
    } catch (error) {
      setError('Error saving settings, please try again');
      setIsLoading(false);
      return null;
    }
  };

  const handlePublish = async () => {
    setError(null);
    setIsLoading(true);

    try {
      track('[Webhook Modal]: Publish Clicked');
      const webhookSuccess = await sendCustomWebhookApi({
        webhookUrl: inputWebhookUrl,
        data: {
          contentKit: {
            thumbnail: {
              square: HIGHLIGHT_SQUARE_THUMBNAIL?.imageUrl,
              portrait: HIGHLIGHT_PORTRAIT_THUMBNAIL?.imageUrl,
              landscape: HIGHLIGHT_THUMBNAIL?.imageUrl,
            },
            video: {
              square: getCDNLink(HIGHLIGHT_EDITED_SQUARE?.downloadUrl),
              portrait: getCDNLink(HIGHLIGHT_EDITED_PORTRAIT?.downloadUrl),
              landscape: getCDNLink(HIGHLIGHT_EDITED_LANDSCAPE?.downloadUrl),
            },
            transcriptText: sentences.map((s) => s.text).join(' '),
            webpageUrl: slug
              ? `https://app.zync.ai/videopage/${slug}`
              : `https://app.zync.ai/${workspaceId}/videofeed/${highlightId}`,
          },
        },
        workspaceId,
        meetingSeriesId,
      });

      if (webhookSuccess) {
        const updatedSeries = await handleSaveSeriesSettings();

        if (!updatedSeries) return;

        refreshSeries(undefined, (series) =>
          localDispatch({
            type: 'REFRESH_SERIES',
            series,
          })
        );
        setIsLoading(false);
      } else {
        setError('Error response from webhook');
        setIsLoading(false);
      }
    } catch (error) {
      setError('Error response from webhook');
      setIsLoading(false);
    }
  };

  if (highlights.length === 0) {
    return (
      <ModalWindow size={Modal.size.xl} onCancel={handleClose}>
        <ModalTitle>No content kit created</ModalTitle>
      </ModalWindow>
    );
  }

  return (
    <ModalWindow size={Modal.size.xl} overflow="overflow-visible">
      <div className="flex flex-col px-11 py-9 h-[274px] overflow-auto purple-scrollbar">
        <button
          className="self-end text-2xl text-blue-gray absolute top-2 right-6"
          onClick={handleClose}
        >
          ×
        </button>
        <div className="flex justify-between w-full">
          <div className="flex flex-col grow font-[Poppins] lg:w-[520px] lg:max-w-[520px] md:w-[400px] md:max-w-[400px] w-[300px] max-w-[300px]">
            <span className="text-black text-2xl font-bold">{title}</span>
            <div className="flex flex-col mt-3">
              <span className="text-black">Webhook URL</span>
              <TextInputRaw
                value={inputWebhookUrl}
                onChange={(event, value) => {
                  setInputWebhookUrl(value);
                }}
                placeholder="https://hooks.zapier.com/hooks/catch/[id]"
                disabled={!isWebhookFeatureAvailable}
                onBlur={() => {
                  track('[Webhook Modal]: Updated Webhook Url');
                }}
              />
            </div>
            <div className="mt-4">
              <span className="text-black text-xs">
                Learn more about webhooks and see sample data{' '}
              </span>
              <a
                className="text-purple underline decoration-purple text-xs"
                href="https://zync.ai/university"
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
            </div>
          </div>
          <div className="flex flex-col w-fit">
            {!isWebhookFeatureAvailable ? (
              <div className="flex flex-col w-full h-full gap-2 items-center">
                <Link
                  className="w-[247px] h-[40px] relative flex bg-purple hover:bg-gradient-to-r hover:from-[#5C41FF] hover:to-[#9820A3] rounded-[10px] transition-colors duration-500 overflow-hidden font-montserrat text-white group"
                  to={`/workspace/billing?workspaceId=${workspaceId}`}
                >
                  <span className="flex w-full h-full items-center justify-center font-bold transition-transform duration-500 ease-in-out transform group-hover:-translate-y-full">
                    PUBLISH
                  </span>
                  <div className="absolute -left-full flex flex-col transform transition-transform duration-500 ease-in-out group-hover:translate-x-[155%] text-left top-1/2 -translate-y-1/2">
                    <span className="text-xxs text-white">Subscribe to</span>
                    <span className="text-xs text-white">
                      ZYNC SOLO PROFESSIONAL
                    </span>
                  </div>
                  <div className="absolute -right-10 transform transition-transform duration-500 ease-in-out group-hover:-translate-x-[180%] top-1/2 -translate-y-1/2 -rotate-90 group-hover:rotate-0">
                    <ArrowRightIcon />
                  </div>
                </Link>
                <span className="font-[Poppins] text-[#617182] font-medium">
                  Publish to Webhook
                </span>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5 items-end">
                <button
                  className={classNames(
                    'text-white text-[13px] flex gap-2 items-center px-3 py-2.5',
                    isPublishButtonDisabled ? 'bg-[#C8AAF4]' : 'bg-purple'
                  )}
                  disabled={isPublishButtonDisabled}
                  onClick={() => {
                    handlePublish();
                  }}
                >
                  {isLoading ? (
                    <LoadingSpinner width="20px" />
                  ) : (
                    <>
                      <CloudUploadIcon /> Publish
                    </>
                  )}
                </button>
              </div>
            )}
            {isWebhookFeatureAvailable &&
              (lastPublishedDate || contentKitLastPublishedDate) && (
                <div className="flex flex-col text-black font-[Poppins] text-xxs items-end">
                  <span>Last Published:</span>
                  <span>
                    {formatPublishedDate(
                      contentKitLastPublishedDate &&
                        (!lastPublishedDate ||
                          contentKitLastPublishedDate > lastPublishedDate)
                        ? contentKitLastPublishedDate
                        : lastPublishedDate
                    )}
                  </span>
                </div>
              )}
            {error ? (
              <span className="text-red font-medium text-xxs">{error}</span>
            ) : null}
          </div>
        </div>
      </div>
    </ModalWindow>
  );
};
